import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = [
    { href: "/", label: t('footer.home') },
    { href: "/acheter", label: language === 'fr' ? 'Acheter' : 'Buy' },
    { href: "/louer", label: language === 'fr' ? 'Louer' : 'Rent' },
    { href: "/estimation", label: language === 'fr' ? 'Vendre' : 'Sell' },
    { href: "/off-market", label: t('nav.offMarket'), isExclusive: true },
    { href: "/contact", label: t('nav.contact') },
  ];

  // Refined starburst icon for exclusive items
  const ExclusiveIcon = () => (
    <svg 
      width="16" 
      height="16" 
      viewBox="0 0 24 24" 
      fill="none" 
      className="inline-block mr-3"
      style={{ marginBottom: '0.12em' }}
    >
      {/* 8-point starburst - refined and minimal */}
      <path 
        d="M12 0L13.5 9.5L22 8L15 12L22 16L13.5 14.5L12 24L10.5 14.5L2 16L9 12L2 8L10.5 9.5L12 0Z" 
        fill="#D4B896"
      />
    </svg>
  );

  const isHomePage = location.pathname === "/";
  
  // Header background logic:
  // - Always solid beige background on ALL pages (no special homepage treatment)
  const headerBg = "bg-byron-bg";

  // Text color logic:
  // - All pages: dark (solid header)
  const textColor = "text-[#1A1A1A]";
  
  // Logo source - transparent wordmark (light background)
  const logoSrc = "/byron-logo-transparent-26.svg";

  return (
    <>
      {/* Minimal Header - Aman Style */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${headerBg}`}
        data-testid="navigation"
      >
        <div className="container-byron">
          <div className="relative flex items-center header-bar">
            {/* Menu Button - Left */}
            <div className="flex-1 flex items-center justify-start">
              <button
                onClick={() => setIsMenuOpen(true)}
                className={`flex items-center gap-3 ${textColor} transition-colors duration-300 group`}
                style={{ fontFamily: 'var(--font-ui)' }}
                aria-label="Open menu"
                data-testid="menu-button"
              >
                <div className="flex flex-col gap-1.5">
                  <span className="w-6 h-px bg-current transition-all group-hover:w-5" />
                  <span className="w-6 h-px bg-current" />
                  <span className="w-6 h-px bg-current" />
                </div>
                <span className="text-[11px] uppercase tracking-[0.18em] font-medium hidden sm:block">Menu</span>
              </button>
            </div>

            {/* Logo - Center (mobile stays scrolled size; desktop grows then shrinks) */}
            <Link 
              to="/" 
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              data-testid="nav-logo"
            >
              <img 
                src={logoSrc}
                alt="Byrōn"
                className={`w-auto transition-[height] duration-500 ease-out ${
                  isScrolled 
                    ? "h-6 md:h-7" 
                    : "h-6 md:h-10"
                }`}
              />
            </Link>

            {/* Right Side - Language & Contact */}
            <div className="flex-1 flex items-center justify-end gap-4 md:gap-6">
              <button 
                onClick={toggleLanguage}
                className={`text-xs uppercase tracking-[0.15em] ${textColor} transition-opacity hidden sm:flex items-center gap-1`}
                data-testid="language-toggle"
              >
                <span className={language === 'en' ? 'opacity-100' : 'opacity-50 hover:opacity-70'}>EN</span>
                <span className="opacity-30">/</span>
                <span className={language === 'fr' ? 'opacity-100' : 'opacity-50 hover:opacity-70'}>FR</span>
              </button>
              <Link 
                to="/contact" 
                className={`${textColor} opacity-70 hover:opacity-100 transition-opacity`}
                data-testid="nav-contact"
              >
                {/* Icon on mobile, text on larger screens */}
                <Mail size={18} strokeWidth={1.5} className="sm:hidden" />
                <span className="hidden sm:inline text-xs uppercase tracking-[0.15em]">
                  {t('nav.contact')}
                </span>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Full Screen Menu Overlay - Aman Style Light Cream */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[100] bg-[#FAF9F6]"
            data-testid="menu-overlay"
          >
            {/* Top Bar with Close + Logo */}
            <div className="relative">
              <div className="container-byron">
                <div className="relative flex items-center header-bar">
                  {/* Close Button - Left */}
                  <div className="flex-1 flex items-center justify-start">
                    <button
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 text-[#1A1A1A] group cursor-pointer"
                      style={{ fontFamily: 'var(--font-ui)' }}
                      aria-label="Close menu"
                      data-testid="menu-close"
                    >
                      <div className="relative w-5 h-5">
                        <span className="absolute top-1/2 left-0 w-5 h-px bg-[#1A1A1A] rotate-45" />
                        <span className="absolute top-1/2 left-0 w-5 h-px bg-[#1A1A1A] -rotate-45" />
                      </div>
                    </button>
                  </div>

                  {/* Logo - Center */}
                  <Link 
                    to="/" 
                    onClick={() => setIsMenuOpen(false)}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    data-testid="menu-logo"
                  >
                    <img 
                      src="/byron-logo-transparent-26.svg"
                      alt="Byrōn"
                      className="h-7 md:h-8 w-auto"
                    />
                  </Link>

                  {/* Empty space for balance */}
                  <div className="flex-1" />
                </div>
              </div>
              
              {/* Thin divider line */}
              <div className="border-b border-[#E5E0D8]" />
            </div>

            {/* Navigation Links - Left aligned */}
            <div className="container-byron pt-12 md:pt-16">
              <nav style={{ fontFamily: 'var(--font-ui)' }}>
                {/* Primary Navigation */}
                <div className="space-y-1">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.05 + index * 0.04 }}
                    >
                      <Link
                        to={link.href}
                        onClick={() => setIsMenuOpen(false)}
                        className={`block py-2 text-lg md:text-xl font-light tracking-wide transition-colors duration-300 ${
                          link.isExclusive 
                            ? "text-[#8B7355] hover:text-[#6B5A45]" 
                            : `text-[#1A1A1A] hover:text-[#8B7355] ${location.pathname === link.href ? "text-[#8B7355]" : ""}`
                        }`}
                        data-testid={`menu-link-${link.label.toLowerCase().replace(' ', '-')}`}
                      >
                        {link.isExclusive && <ExclusiveIcon />}
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Secondary Links */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                  className="mt-12 pt-8 border-t border-[#E5E0D8] space-y-1"
                >
                  <Link
                    to="/rechercher"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-2 text-base font-light text-[#666] hover:text-[#1A1A1A] transition-colors"
                  >
                    {language === 'fr' ? 'Rechercher' : 'Search'}
                  </Link>
                </motion.div>
              </nav>
            </div>

            {/* Bottom Section */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="absolute bottom-0 left-0 right-0 bg-[#F5F3EE] py-8"
            >
              <div className="container-byron">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  {/* Contact Info */}
                  <div className="text-sm text-[#666]" style={{ fontFamily: 'var(--font-ui)' }}>
                    <p>Chemin de Byron 12, 1223 Cologny, Geneva</p>
                    <p className="mt-1">
                      <a href="tel:+41227001234" className="hover:text-[#1A1A1A] transition-colors">
                        +41 22 700 12 34
                      </a>
                    </p>
                  </div>

                  {/* Language Switch */}
                  <div className="flex gap-4 text-sm" style={{ fontFamily: 'var(--font-ui)' }}>
                    <button 
                      onClick={() => { if (language !== 'en') toggleLanguage(); }}
                      className={`${language === 'en' ? 'text-[#1A1A1A] font-medium' : 'text-[#999] hover:text-[#1A1A1A]'} transition-colors`}
                    >
                      EN
                    </button>
                    <button 
                      onClick={() => { if (language !== 'fr') toggleLanguage(); }}
                      className={`${language === 'fr' ? 'text-[#1A1A1A] font-medium' : 'text-[#999] hover:text-[#1A1A1A]'} transition-colors`}
                    >
                      FR
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
